/**
 * The language cheat sheet.
 *
 * Kept inside the app on purpose: the reference someone needs while writing
 * pseudocode is the one they can reach without leaving the editor. The long
 * form lives in LANGUAGE.md.
 */
import { Dialog, Kbd } from '../ui';
import { BRANDING } from '../../config/branding';
import type { Translator } from '../../i18n';

export interface HelpDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly t: Translator['t'];
}

interface Entry {
  readonly code: string;
  readonly note: string;
}

const SYNTAX: readonly { heading: string; entries: readonly Entry[] }[] = [
  {
    heading: 'Steps and terminators',
    entries: [
      { code: 'Read user data;', note: 'Any text that is not a keyword becomes a step.' },
      { code: 'start;   Start app;', note: 'Entry terminator. The whole phrase is the label.' },
      { code: 'end;   End the diagram;', note: 'Exit terminator. A branch may have its own.' },
    ],
  },
  {
    heading: 'Decisions',
    entries: [
      { code: 'if (Ready?)\n{\n    Ship it;\n}', note: 'A diamond with a Yes branch.' },
      { code: 'else if (Later?) { … }\nelse { … }', note: 'Chains read as successive diamonds.' },
      {
        code: 'if (Paid?) yes "Approved" no "Declined"',
        note: 'Custom branch labels, per decision.',
      },
    ],
  },
  {
    heading: 'Loops',
    entries: [
      { code: 'while (More items?)\n{\n    Handle one;\n}', note: 'Tests before each pass.' },
      { code: 'repeat\n{\n    Try;\n} until (Done?);', note: 'Runs at least once.' },
      { code: 'for each (Line in order)\n{\n    Pick it;\n}', note: 'Iterates a collection.' },
      { code: 'break;   continue;', note: 'Only inside a loop (break also works in a switch).' },
    ],
  },
  {
    heading: 'Branching on a value',
    entries: [
      {
        code: 'switch (Role)\n{\n    case "Admin": { … }\n    default: { … }\n}',
        note: 'One labelled edge per case.',
      },
    ],
  },
  {
    heading: 'Functions',
    entries: [
      { code: 'function greet\n{\n    Say hello;\n}', note: 'Not in the flow until it is called.' },
      { code: 'call greet;', note: 'A subprocess node; expands inline by default.' },
      { code: 'call validateUser(order);', note: 'Arguments are shown, never evaluated.' },
      { code: 'return;   return Result;', note: 'Leaves the function.' },
    ],
  },
  {
    heading: 'Comments and metadata',
    entries: [
      { code: '// a note\n/* several\n   lines */', note: 'Comments never become nodes.' },
      { code: '@color("#2563eb")\n@icon("user")\nCreate user;', note: 'Styles the node below.' },
      {
        code: '@shape("io")   @note("…")   @id("x")',
        note: 'Values are validated against a list.',
      },
    ],
  },
];

const SHORTCUTS: readonly { keys: string; labelKey: Parameters<Translator['t']>[0] }[] = [
  { keys: 'Ctrl N', labelKey: 'shortcuts.newProject' },
  { keys: 'Ctrl O', labelKey: 'shortcuts.openProjects' },
  { keys: 'Ctrl S', labelKey: 'shortcuts.export' },
  { keys: 'Ctrl Z', labelKey: 'shortcuts.undo' },
  { keys: 'Ctrl Shift Z', labelKey: 'shortcuts.redo' },
  { keys: 'Shift Alt F', labelKey: 'shortcuts.formatDocument' },
  { keys: 'Ctrl Enter', labelKey: 'shortcuts.updateNow' },
  { keys: 'Ctrl F', labelKey: 'shortcuts.find' },
  { keys: 'Ctrl 0', labelKey: 'shortcuts.fitToScreen' },
  { keys: 'Ctrl =', labelKey: 'shortcuts.zoomIn' },
  { keys: 'Ctrl -', labelKey: 'shortcuts.zoomOut' },
  { keys: 'F6', labelKey: 'shortcuts.focusDiagram' },
  { keys: 'Ctrl Shift M', labelKey: 'shortcuts.focusProblems' },
  { keys: 'Ctrl Shift L', labelKey: 'shortcuts.togglePanelLayout' },
  { keys: 'Ctrl Shift D', labelKey: 'shortcuts.toggleTheme' },
  { keys: 'F1', labelKey: 'shortcuts.openHelp' },
];

export function HelpDialog({ open, onClose, t }: HelpDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('toolbar.help')}
      description={BRANDING.tagline}
      size="lg"
    >
      <div className="flex flex-col gap-7">
        {SYNTAX.map((group) => (
          <section key={group.heading} className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold tracking-wider text-ink-muted uppercase">
              {group.heading}
            </h3>
            <dl className="flex flex-col gap-2">
              {group.entries.map((entry) => (
                <div
                  key={entry.code}
                  className="grid gap-1 rounded-md bg-surface-sunken p-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-baseline sm:gap-4"
                >
                  <dt>
                    <pre className="overflow-x-auto font-mono text-xs whitespace-pre text-ink">
                      {entry.code}
                    </pre>
                  </dt>
                  <dd className="text-xs text-ink-muted">{entry.note}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold tracking-wider text-ink-muted uppercase">
            {t('shortcuts.title')}
          </h3>
          <ul className="grid gap-1 sm:grid-cols-2">
            {SHORTCUTS.map((shortcut) => (
              <li
                key={shortcut.keys}
                className="flex items-center justify-between gap-3 rounded px-1 py-1 text-sm"
              >
                <span className="text-ink-muted">{t(shortcut.labelKey)}</span>
                <span className="flex shrink-0 gap-1">
                  {shortcut.keys.split(' ').map((key) => (
                    <Kbd key={key}>{key}</Kbd>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Dialog>
  );
}
